import { MasterGroupSingleResponse, IMasterGroup, IMasterGroupContext, isFunction, isPromiseLike } from '@ngt/opms';
import { useMemo } from 'react';
import { KeyedMutator, MutatorCallback } from 'swr';
import { GetConsentEventDataResponse, MasterGroup } from '../../api/dtos';
import useConsentEventData from '../data/useConsentEventData';



const useGenerateEConsentMasterGroupContext = (consentEventContext: ReturnType<typeof useConsentEventData>) => {
    
    const masterGroupContext: IMasterGroupContext = useMemo(() => {
        const masterGroup = consentEventContext.data?.masterGroup as IMasterGroup;
        const error = consentEventContext.error;

        const masterGroupMutate: KeyedMutator<MasterGroupSingleResponse> = async (data, opts) => {
            let newData: GetConsentEventDataResponse = { ...(consentEventContext.data as any), responseStatus: error as any };
            let newSingleData: MasterGroupSingleResponse | undefined = undefined;
            
            if (!data) {
                newSingleData = undefined;
            }
            else if (isFunction(data)) {
                const existingSingleData = { masterGroup, responseStatus: error } as MasterGroupSingleResponse;

                const callback = (data as MutatorCallback<MasterGroupSingleResponse>)(existingSingleData);
                
                if (isPromiseLike(callback)) {
                    newSingleData = await (callback as Promise<MasterGroupSingleResponse>);
                }
                else {
                    newSingleData = callback;
                }
            }
            else if (isPromiseLike(data)) {
                newSingleData = await (data as Promise<MasterGroupSingleResponse>);
            }

            if (!newSingleData) {
                newData.masterGroup = undefined as any;
            }
            else {
                if (newSingleData.responseStatus) {
                    newData.responseStatus = newSingleData.responseStatus;
                }

                newData.masterGroup = newSingleData.masterGroup as MasterGroup;
            }

            const updatedData = await consentEventContext.mutate(newData, opts as any);

            if (updatedData) {
                return {
                    masterGroup: updatedData.masterGroup,
                    responseStatus: updatedData.responseStatus
                } as MasterGroupSingleResponse
            }

            return undefined;
        }

        const masterGroupRefresh = async () => {
            const newData = await consentEventContext.refresh();

            return newData?.masterGroup;
        }

        return {
            data: masterGroup,
            error: consentEventContext.error,
            loading: consentEventContext.loading,
            validating: consentEventContext.validating,
            mutate: masterGroupMutate,
            refresh: masterGroupRefresh
        }
    }, [consentEventContext])

    return masterGroupContext
}

export default useGenerateEConsentMasterGroupContext;